import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import { toInputDateValue } from './superadminDateUtils';
import './SuperadminStyles.css';

const parseApiErrors = (data) => {
    const next = {};
    if (!data) return next;

    if (data.errors && typeof data.errors === 'object') {
        Object.keys(data.errors).forEach((key) => {
            const val = data.errors[key];
            next[key] = Array.isArray(val) ? val[0] : val;
        });
    }

    if (data.message) {
        next.general = data.message;
    } else if (Object.keys(next).length > 0) {
        next.general = Object.values(next)[0];
    }

    return next;
};

const ClienteForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEditMode = !!id;
    const [formData, setFormData] = useState({
        nombre: '',
        nit: '',
        razon_social: '',
        direccion: '',
        telefono: '',
        email_contacto: '',
        limite_usuarios: '',
        usuarios_medicos_disponibles: 2,
        usuarios_administrativos_disponibles: 3,
        estado: 0,
        tipo_contrato: 'mensual',
        fecha_vencimiento: '',
        almacenamiento_limite_gb: 0,
    });
    const [originalMedicos, setOriginalMedicos] = useState(2);
    const [originalAdmin, setOriginalAdmin] = useState(3);
    const [usuariosMedicosCreados, setUsuariosMedicosCreados] = useState(0);
    const [usuariosAdminCreados, setUsuariosAdminCreados] = useState(0);
    const [usuariosActuales, setUsuariosActuales] = useState(0);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEditMode);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        if (isEditMode) {
            fetchCliente();
        }
    }, [history, id, isEditMode]);

    const fetchCliente = async () => {
        try {
            const response = await axios.get(`/superadmin/clientes/${id}`);
            const cliente = response.data;
            setFormData({
                nombre: cliente.nombre || '',
                nit: cliente.nit || '',
                razon_social: cliente.razon_social || '',
                direccion: cliente.direccion || '',
                telefono: cliente.telefono || '',
                email_contacto: cliente.email_contacto || '',
                limite_usuarios: cliente.limite_usuarios || '',
                usuarios_medicos_disponibles: cliente.usuarios_medicos_disponibles ?? 2,
                usuarios_administrativos_disponibles: cliente.usuarios_administrativos_disponibles ?? 3,
                estado: cliente.estado != null ? Number(cliente.estado) : 0,
                tipo_contrato: cliente.tipo_contrato || 'mensual',
                fecha_vencimiento: toInputDateValue(cliente.fecha_vencimiento),
                almacenamiento_limite_gb: cliente.almacenamiento_limite_gb ?? 0,
            });
            setOriginalMedicos(cliente.usuarios_medicos_disponibles ?? 2);
            setOriginalAdmin(cliente.usuarios_administrativos_disponibles ?? 3);
            setUsuariosMedicosCreados(cliente.usuarios_medicos_creados ?? 0);
            setUsuariosAdminCreados(cliente.usuarios_administrativos_creados ?? 0);
            setUsuariosActuales(cliente.usuarios_actuales || 0);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching cliente:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            } else {
                setErrors({ general: 'Error al cargar el cliente' });
                setLoading(false);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        let parsed = value;
        if (type === 'number') {
            parsed = parseInt(value, 10) || '';
        } else if (name === 'almacenamiento_limite_gb') {
            parsed = value === '' ? 0 : parseFloat(value);
        } else if (name === 'estado') {
            parsed = parseInt(value, 10);
        }
        setFormData({
            ...formData,
            [name]: parsed,
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.nit.trim()) {
            newErrors.nit = 'El NIT es requerido';
        }

        if (!formData.email_contacto.trim()) {
            newErrors.email_contacto = 'El email de contacto es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_contacto)) {
            newErrors.email_contacto = 'El email no tiene un formato válido';
        }

        const medicos = parseInt(formData.usuarios_medicos_disponibles, 10) || 0;
        const admin = parseInt(formData.usuarios_administrativos_disponibles, 10) || 0;
        const total = medicos + admin;

        if (medicos < 0 || admin < 0) {
            newErrors.usuarios_medicos_disponibles = 'Los cupos no pueden ser negativos';
        }

        if (total < 1) {
            newErrors.usuarios_medicos_disponibles = 'Debe asignar al menos un cupo (médico o administrativo)';
        }

        if (isEditMode && medicos < usuariosMedicosCreados) {
            newErrors.usuarios_medicos_disponibles = `No puede ser menor a los cupos médicos ya asignados (${usuariosMedicosCreados})`;
        }

        if (isEditMode && admin < usuariosAdminCreados) {
            newErrors.usuarios_administrativos_disponibles = `No puede ser menor a los cupos administrativos ya asignados (${usuariosAdminCreados})`;
        }

        if (isEditMode && total < usuariosActuales) {
            newErrors.usuarios_medicos_disponibles = `El total (${total}) no puede ser menor a los usuarios activos (${usuariosActuales})`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        const payload = {
            ...formData,
            usuarios_medicos_disponibles: parseInt(formData.usuarios_medicos_disponibles, 10) || 0,
            usuarios_administrativos_disponibles: parseInt(formData.usuarios_administrativos_disponibles, 10) || 0,
            limite_usuarios:
                (parseInt(formData.usuarios_medicos_disponibles, 10) || 0) +
                (parseInt(formData.usuarios_administrativos_disponibles, 10) || 0),
            estado: parseInt(formData.estado, 10),
            fecha_vencimiento: formData.fecha_vencimiento || null,
            almacenamiento_limite_gb: parseFloat(formData.almacenamiento_limite_gb) || 0,
        };

        try {
            if (isEditMode) {
                await axios.put(`/superadmin/clientes/${id}`, payload);
            } else {
                await axios.post('/superadmin/clientes', payload);
            }
            history.push('/superadmin/clientes');
        } catch (error) {
            console.error('Error saving cliente:', error);
            setErrors(parseApiErrors(error.response?.data));
            setSubmitting(false);
        }
    };

    const totalCupos =
        (parseInt(formData.usuarios_medicos_disponibles, 10) || 0) +
        (parseInt(formData.usuarios_administrativos_disponibles, 10) || 0);

    const showCuposWarning = () => {
        if (!isEditMode) return false;
        return (
            (formData.usuarios_medicos_disponibles < originalMedicos ||
                formData.usuarios_administrativos_disponibles < originalAdmin) &&
            totalCupos >= usuariosActuales
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-building"></i> {isEditMode ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h4>
                <Link to="/superadmin/clientes" className="btn btn-secondary">
                    <i className="fas fa-arrow-left"></i> Volver
                </Link>
            </div>

            {errors.general && (
                <div className="alert alert-danger">
                    {errors.general}
                </div>
            )}

            {showCuposWarning() && (
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i> Está reduciendo cupos de usuario.
                    Actualmente hay {usuariosActuales} usuario(s) activo(s) y {usuariosMedicosCreados} médico(s) / {usuariosAdminCreados} administrativo(s) ya parametrizados.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h5>Información Básica</h5>
                    
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Nombre del cliente"
                                />
                                {errors.nombre && (
                                    <div className="invalid-feedback">{errors.nombre}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nit">NIT/Documento <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nit ? 'is-invalid' : ''}`}
                                    id="nit"
                                    name="nit"
                                    value={formData.nit}
                                    onChange={handleChange}
                                    placeholder="NIT o documento de identificación"
                                />
                                {errors.nit && (
                                    <div className="invalid-feedback">{errors.nit}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="razon_social">Razón Social</label>
                        <input
                            type="text"
                            className="form-control"
                            id="razon_social"
                            name="razon_social"
                            value={formData.razon_social}
                            onChange={handleChange}
                            placeholder="Razón social del cliente"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="direccion">Dirección</label>
                        <input
                            type="text"
                            className="form-control"
                            id="direccion"
                            name="direccion"
                            value={formData.direccion}
                            onChange={handleChange}
                            placeholder="Dirección física"
                        />
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="telefono"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono de contacto"
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="email_contacto">Email de Contacto <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email_contacto ? 'is-invalid' : ''}`}
                                    id="email_contacto"
                                    name="email_contacto"
                                    value={formData.email_contacto}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                />
                                {errors.email_contacto && (
                                    <div className="invalid-feedback">{errors.email_contacto}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Configuración</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="usuarios_medicos_disponibles">Cupos Médicos <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.usuarios_medicos_disponibles ? 'is-invalid' : ''}`}
                                    id="usuarios_medicos_disponibles"
                                    name="usuarios_medicos_disponibles"
                                    value={formData.usuarios_medicos_disponibles}
                                    onChange={handleChange}
                                    min="0"
                                />
                                {errors.usuarios_medicos_disponibles && (
                                    <div className="invalid-feedback">{errors.usuarios_medicos_disponibles}</div>
                                )}
                                {isEditMode && (
                                    <small className="form-text text-muted">
                                        Asignados: {usuariosMedicosCreados}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="usuarios_administrativos_disponibles">Cupos Administrativos <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.usuarios_administrativos_disponibles ? 'is-invalid' : ''}`}
                                    id="usuarios_administrativos_disponibles"
                                    name="usuarios_administrativos_disponibles"
                                    value={formData.usuarios_administrativos_disponibles}
                                    onChange={handleChange}
                                    min="0"
                                />
                                {errors.usuarios_administrativos_disponibles && (
                                    <div className="invalid-feedback">{errors.usuarios_administrativos_disponibles}</div>
                                )}
                                {isEditMode && (
                                    <small className="form-text text-muted">
                                        Asignados: {usuariosAdminCreados}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Total licencias</label>
                                <input
                                    type="number"
                                    className="form-control bg-light"
                                    value={totalCupos}
                                    readOnly
                                />
                                <small className="form-text text-muted">
                                    Suma automática (reemplaza el límite único anterior)
                                </small>
                                {isEditMode && (
                                    <small className="form-text text-muted d-block">
                                        Personal activo: {usuariosActuales}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="estado">Estado</label>
                                <select
                                    className="form-control"
                                    id="estado"
                                    name="estado"
                                    value={formData.estado}
                                    onChange={handleChange}
                                >
                                    <option value={0}>Activo</option>
                                    <option value={1}>Pendiente pago (bloqueado)</option>
                                    <option value={2}>Retirado</option>
                                    <option value={3}>Pendiente pago activo</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="fecha_vencimiento">Fecha de Vencimiento</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="fecha_vencimiento"
                                    name="fecha_vencimiento"
                                    value={formData.fecha_vencimiento}
                                    onChange={handleChange}
                                />
                                <small className="form-text text-muted">Opcional</small>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="tipo_contrato">Tipo de Contrato</label>
                                <select
                                    className="form-control"
                                    id="tipo_contrato"
                                    name="tipo_contrato"
                                    value={formData.tipo_contrato}
                                    onChange={handleChange}
                                >
                                    <option value="mensual">Mensual</option>
                                    <option value="trimestral">Trimestral</option>
                                    <option value="semestral">Semestral</option>
                                    <option value="anual">Anual</option>
                                </select>
                                <small className="form-text text-muted">Útil para reportes junto con fecha de vencimiento</small>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="almacenamiento_limite_gb">Almacenamiento archivos clínicos (GB)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="almacenamiento_limite_gb"
                                    name="almacenamiento_limite_gb"
                                    value={formData.almacenamiento_limite_gb}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.5"
                                />
                                <small className="form-text text-muted">
                                    Imágenes y PDF por paciente. <strong>0</strong> = módulo deshabilitado.
                                    {isEditMode && formData.almacenamiento_limite_gb > 0 && (
                                        <span className="d-block">Consumo actual: ver panel Archivos clínicos o ficha del cliente.</span>
                                    )}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <button
                        type="submit"
                        className="btn btn-superadmin"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> {isEditMode ? 'Actualizar Cliente' : 'Crear Cliente'}
                            </>
                        )}
                    </button>
                    <Link to="/superadmin/clientes" className="btn btn-secondary ml-2">
                        Cancelar
                    </Link>
                </div>
            </form>
        </SuperadminLayout>
    );
};

export default ClienteForm;
